import { useRef, useCallback } from 'react';
import { scriptUtils } from '@sendible/common';
import type { GoogleApiLoaderConfig, GoogleApiLoaderState } from './types';

export const useGoogleApiLoader = () => {
  const stateRef = useRef<GoogleApiLoaderState>({
    isGapiLoaded: false,
    loadPromise: null,
  });

  const loadGapi = useCallback((config: GoogleApiLoaderConfig): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (stateRef.current.isGapiLoaded) {
        resolve();
      }

      scriptUtils
        .addScript('https://apis.google.com/js/api.js', {
          async: true,
          defer: true,
        })
        .then(() => {
          window.gapi.load('client:picker', async () => {
            try {
              await window.gapi.client.init({
                apiKey: config.apiKey,
                discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/youtube/v3/rest'],
              });

              stateRef.current.isGapiLoaded = true;
              resolve();
            } catch (error) {
              reject(error);
            }
          });
        })
        .catch(reject);
    });
  }, []);

  const loadGoogleApis = useCallback(
    async (config: GoogleApiLoaderConfig): Promise<void> => {
      if (stateRef.current.loadPromise) {
        return stateRef.current.loadPromise;
      }

      const promise = loadGapi(config)
        .then(() => undefined)
        .finally(() => {
          stateRef.current.loadPromise = null;
        });

      stateRef.current.loadPromise = promise;

      return promise;
    },
    [loadGapi]
  );

  const isLoaded = useCallback((): boolean => {
    return stateRef.current.isGapiLoaded;
  }, []);

  return {
    loadGoogleApis,
    isLoaded,
  };
};
